import { Component } from '@theme/component';
import {
  debounce,
  isClickedOutside,
  onAnimationEnd,
} from '@theme/utilities';

/**
 * A custom element that manages a dialog.
 *
 * This version does not lock the body scroll when the dialog opens.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog – The dialog element.
 *
 * @extends Component<Refs>
 */
export class DialogComponent extends Component {
  requiredRefs = ['dialog'];

  connectedCallback() {
    super.connectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.addEventListener(
        'resize',
        this.#handleResize
      );
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    if (this.minWidth || this.maxWidth) {
      window.removeEventListener(
        'resize',
        this.#handleResize
      );
    }

    this.removeEventListener(
      'click',
      this.#handleClick
    );

    this.removeEventListener(
      'keydown',
      this.#handleKeyDown
    );
  }

  #handleResize = debounce(() => {
    const { minWidth, maxWidth } = this;

    if (!minWidth && !maxWidth) {
      return;
    }

    const windowWidth = window.innerWidth;

    if (
      windowWidth < minWidth ||
      windowWidth > maxWidth
    ) {
      this.closeDialog();
    }
  }, 50);

  /**
   * Shows the dialog without locking the page scroll.
   */
  showDialog() {
    const { dialog } = this.refs;

    if (dialog.open) {
      return;
    }

    try {
      dialog.showModal();
    } catch (error) {
      throw error;
    }

    this.dispatchEvent(new DialogOpenEvent());

    /**
     * Wait until the next tick to avoid a race condition when
     * showDialog is called inside another click handler.
     */
    setTimeout(() => {
      if (!dialog.open) {
        return;
      }

      this.addEventListener(
        'click',
        this.#handleClick
      );

      this.addEventListener(
        'keydown',
        this.#handleKeyDown
      );
    });
  }

  /**
   * Closes the dialog.
   */
  closeDialog = async () => {
    const { dialog } = this.refs;

    if (!dialog.open) {
      return;
    }

    this.removeEventListener(
      'click',
      this.#handleClick
    );

    this.removeEventListener(
      'keydown',
      this.#handleKeyDown
    );

    dialog.classList.add('dialog-closing');

    await onAnimationEnd(dialog, undefined, {
      subtree: false,
    });

    if (dialog.open) {
      dialog.close();
    }

    dialog.classList.remove('dialog-closing');

    this.dispatchEvent(new DialogCloseEvent());
  };

  /**
   * Toggles the dialog.
   */
  toggleDialog = () => {
    if (this.refs.dialog.open) {
      this.closeDialog();
    } else {
      this.showDialog();
    }
  };

  /**
   * Closes the dialog when the user clicks outside of it.
   *
   * @param {MouseEvent} event - The mouse event.
   */
  #handleClick(event) {
    const { dialog } = this.refs;

    if (isClickedOutside(event, dialog)) {
      this.closeDialog();
    }
  }

  /**
   * Closes the dialog when the user presses Escape.
   *
   * @param {KeyboardEvent} event - The keyboard event.
   */
  #handleKeyDown(event) {
    if (event.key !== 'Escape') {
      return;
    }

    event.preventDefault();
    this.closeDialog();
  }

  /**
   * Gets the minimum width of the dialog.
   *
   * @returns {number}
   */
  get minWidth() {
    return Number(
      this.getAttribute('dialog-active-min-width')
    );
  }

  /**
   * Gets the maximum width of the dialog.
   *
   * @returns {number}
   */
  get maxWidth() {
    return Number(
      this.getAttribute('dialog-active-max-width')
    );
  }
}

if (!customElements.get('dialog-component')) {
  customElements.define(
    'dialog-component',
    DialogComponent
  );
}

export class DialogOpenEvent extends CustomEvent {
  constructor() {
    super(DialogOpenEvent.eventName);
  }

  static eventName = 'dialog:open';
}

export class DialogCloseEvent extends CustomEvent {
  constructor() {
    super(DialogCloseEvent.eventName);
  }

  static eventName = 'dialog:close';
}

/**
 * Keep the existing scroll-lock behavior for <details> elements only.
 *
 * Dialogs such as the cart drawer will no longer add scroll-lock
 * to the <html> element.
 */
function updateDetailsScrollLock() {
  const hasOpenScrollLockDetails = Boolean(
    document.querySelector(
      'details[scroll-lock][open]'
    )
  );

  document.documentElement.toggleAttribute(
    'scroll-lock',
    hasOpenScrollLockDetails
  );
}

document.addEventListener(
  'toggle',
  (event) => {
    const target = event.target;

    if (!(target instanceof HTMLDetailsElement)) {
      return;
    }

    if (!target.hasAttribute('scroll-lock')) {
      return;
    }

    updateDetailsScrollLock();
  },
  { capture: true }
);